/**
 * TransferBondsPage Component
 * Main page for transfer bond management with Requesting/Posting tabs
 * Uses centralized OathUI components for consistent styling
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { PlusIcon, MapPinIcon, PhoneIcon, BellIcon, BellAlertIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { useOathAuth } from '../../../services/oathAuthContext';
import { useOathNotifications } from '../../../services/oathNotificationContext';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathAgencySelector,
  OathAgencyInfo,
  OathBanner,
} from '../../shared/OathUI';
import RequestingList from './RequestingList';
import PostingList from './PostingList';
import CreateRequestModal from './CreateRequestModal';
import TerritoriesModal from './posting/TerritoriesModal';
import OathPreferencesModal from '../../shared/OathPreferencesModal';
import TransferBondNotificationsModal from './TransferBondNotificationsModal';

const TransferBondsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useOathAuth();
  const { notifications } = useOathNotifications();
  const notificationCount = notifications.filter((n) => n.contextTypeCode !== 'LB').length;

  // Get initial tab from URL or default to 'requesting'
  const initialTab = searchParams.get('tab') || 'requesting';
  const [activeTab, setActiveTab] = useState(initialTab === 'posting' ? 1 : 0);

  // Agency selection for multi-agency users
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [territoriesModalOpen, setTerritoriesModalOpen] = useState(false);
  const [preferencesModalOpen, setPreferencesModalOpen] = useState(false);
  const [notificationsModalOpen, setNotificationsModalOpen] = useState(false);
  const agencies = useMemo(() => user?.agencies || [], [user?.agencies]);

  // Set default agency on mount - prefer main agency, then canPost, then first
  useEffect(() => {
    if (agencies.length > 0 && !selectedAgencyId) {
      // Find main agency first, then fall back to one with posting privileges, then first agency
      const mainAgency = agencies.find(a => a.isMainAgency);
      const canPostAgency = agencies.find(a => a.canPost);
      const defaultAgency = mainAgency || canPostAgency || agencies[0];
      setSelectedAgencyId(defaultAgency.agencyId);
    }
  }, [agencies, selectedAgencyId]);

  // Update URL when tab changes
  const handleTabChange = (index) => {
    setActiveTab(index);
    const tabValue = index === 0 ? 'requesting' : 'posting';
    setSearchParams({ tab: tabValue });
  };

  // Get selected agency details
  const selectedAgency = agencies.find((a) => a.agencyId === selectedAgencyId);

  return (
    <OathPageWrapper maxWidth="max-w-7xl">
      {/* Header Card */}
      <OathHeaderCard
        title="Transfer Bonds"
        subtitle="Request and manage transfer bonds for your agency"
        icon={ArrowsRightLeftIcon}
        compact
        rightContent={
          <div className="flex items-center gap-3">
            <button
              onClick={() => setNotificationsModalOpen(true)}
              className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              title="Notifications"
            >
              {notificationCount > 0 ? (
                <BellAlertIcon className="h-5 w-5 text-primary dark:text-primary-light" />
              ) : (
                <BellIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              )}
              {notificationCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full">
                  {notificationCount > 99 ? '99+' : notificationCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setPreferencesModalOpen(true)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              title="Contacts & Preferences"
            >
              <PhoneIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <OathAgencySelector
              agencies={agencies}
              selectedAgencyId={selectedAgencyId}
              onSelect={setSelectedAgencyId}
            />
          </div>
        }
      >
        {/* Agency info for single-agency users */}
        {agencies.length === 1 && <OathAgencyInfo agency={selectedAgency} />}
      </OathHeaderCard>

      {/* Main Content Card */}
      <OathContentCard noPadding>
        {/* Tabs */}
        <TabGroup selectedIndex={activeTab} onChange={handleTabChange}>
          <TabList className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <Tab
              className={({ selected }) =>
                `flex-1 py-3 sm:py-4 text-center text-xs sm:text-sm font-medium transition-colors focus:outline-none ${
                  selected
                    ? 'bg-primary text-white font-semibold border-b-2 border-primary-dark'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }`
              }
            >
              Requesting
            </Tab>
            <Tab
              className={({ selected }) =>
                `flex-1 py-3 sm:py-4 text-center text-xs sm:text-sm font-medium transition-colors focus:outline-none ${
                  selected
                    ? 'bg-warning text-white font-semibold border-b-2 border-warning-dark'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }`
              }
            >
              Posting
            </Tab>
          </TabList>

          <TabPanels>
            {/* Requesting Tab */}
            <TabPanel className="p-4 sm:p-6 bg-gray-100 dark:bg-gray-900 sm:bg-white sm:dark:bg-gray-800">
              <OathBanner
                message="Create a new transfer bond request to find a posting agent in your area."
                actionLabel="New Request"
                actionIcon={PlusIcon}
                onAction={() => setCreateModalOpen(true)}
              />
              <RequestingList agencyId={selectedAgencyId} />
            </TabPanel>

            {/* Posting Tab */}
            <TabPanel className="p-4 sm:p-6 bg-gray-100 dark:bg-gray-900 sm:bg-white sm:dark:bg-gray-800">
              <OathBanner
                message="Manage your default quote values and territory settings per county."
                actionLabel="Manage Territories"
                actionIcon={MapPinIcon}
                onAction={() => setTerritoriesModalOpen(true)}
              />
              <PostingList agencyId={selectedAgencyId} />
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </OathContentCard>

      {/* Create Request Modal */}
      <CreateRequestModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          // Modal handles navigation to detail page
        }}
      />

      {/* Territories Modal */}
      <TerritoriesModal
        isOpen={territoriesModalOpen}
        onClose={() => setTerritoriesModalOpen(false)}
        selectedAgency={selectedAgency}
      />

      {/* Contacts & Preferences Modal */}
      <OathPreferencesModal
        isOpen={preferencesModalOpen}
        onClose={() => setPreferencesModalOpen(false)}
        agencies={agencies}
        user={user}
      />

      {/* Notifications Modal */}
      <TransferBondNotificationsModal
        isOpen={notificationsModalOpen}
        onClose={() => setNotificationsModalOpen(false)}
      />
    </OathPageWrapper>
  );
};

export default TransferBondsPage;
